import { useState } from 'react';
import { useAdminSendTestNotification } from '@/api/auth/admin/admin';
import { SendTestNotificationRequestEventData } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Send, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface SendTestNotificationDialogProps {
  eventName: string;
  disabled?: boolean;
}

interface KeyValuePair {
  key: string;
  value: string;
}

/**
 * Dialog for sending a test notification with configurable template variables
 */
export function SendTestNotificationDialog({
  eventName: eventName,
  disabled,
}: SendTestNotificationDialogProps) {
  const [open, setOpen] = useState(false);
  const [pairs, setPairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);

  const { mutate: sendTestNotification, isPending } = useAdminSendTestNotification({
    mutation: {
      onSuccess: () => {
        toast.success('Test notification queued. Check your email.');
        setOpen(false);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'SendTestNotification', eventType: eventName });
        toast.error('Failed to send test: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const handleAddPair = () => {
    setPairs([...pairs, { key: '', value: '' }]);
  };

  const handleRemovePair = (index: number) => {
    setPairs(pairs.filter((_, i) => i !== index));
  };

  const handlePairChange = (index: number, field: 'key' | 'value', value: string) => {
    setPairs(pairs.map((pair, i) => (i === index ? { ...pair, [field]: value } : pair)));
  };

  const handleSend = () => {
    const eventData: SendTestNotificationRequestEventData = {};
    pairs.forEach((pair) => {
      if (pair.key.trim()) {
        eventData[pair.key.trim()] = pair.value as unknown as { [key: string]: unknown };
      }
    });

    sendTestNotification({
      data: {
        eventType: eventName,
        eventData: Object.keys(eventData).length > 0 ? eventData : undefined,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Send className="h-4 w-4 mr-2" />
          Send Test
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Test Notification</DialogTitle>
          <DialogDescription>
            Configure template variables to include in the test notification. These will replace{' '}
            {'{{variableName}}'} placeholders in the template.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-2 text-sm font-medium text-muted-foreground">
            <div>Variable Name</div>
            <div>Value</div>
          </div>
          {pairs.map((pair, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                placeholder="variableName"
                value={pair.key}
                onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                className="font-mono"
              />
              <Input
                placeholder="value"
                value={pair.value}
                onChange={(e) => handlePairChange(index, 'value', e.target.value)}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemovePair(index)}
                disabled={pairs.length === 1}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={handleAddPair}>
            <Plus className="h-4 w-4 mr-2" />
            Add Variable
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isPending}>
            <Send className="h-4 w-4 mr-2" />
            {isPending ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
